import React, { useState } from 'react';
import { FormControlLabel, List, ListItem, Stack, Switch, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AccountData } from '../../api';
import { useCalendarFilter } from './CalendarFilterContext';
import { FilterMenu } from '../../common/filter/FilterMenu';
import { WorkshopsFilterSection } from '../../common/filter/WorkshopsFilterSection';
import { useAuth } from '../../session/InternalAuthProvider';
import { useCalendarFilterSummary } from './useCalendarFilterSummary';

export const CalendarFilter = ({ iconOnly }: { iconOnly?: boolean }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const summary = useCalendarFilterSummary();
  const {
    selectedOrderPageIds,
    setSelectedOrderPageIds,
    showEmployees,
    setShowEmployees,
    showShifts,
    setShowShifts,
    showNotes,
    setShowNotes,
    showUnavailabilities,
    setShowUnavailabilities,
    showOnlyMine,
    setShowOnlyMine,
    isFilterActive,
    resetFilter,
  } = useCalendarFilter();

  // Staged state - only committed on mobile Apply
  const [stagedIds, setStagedIds] = useState<string[] | null>(selectedOrderPageIds);
  const [stagedEmployees, setStagedEmployees] = useState(showEmployees);
  const [stagedShifts, setStagedShifts] = useState(showShifts);
  const [stagedNotes, setStagedNotes] = useState(showNotes);
  const [stagedUnavailabilities, setStagedUnavailabilities] = useState(showUnavailabilities);
  const [stagedOnlyMine, setStagedOnlyMine] = useState(showOnlyMine);

  // On mobile, re-sync staged from committed whenever the dialog opens (via FilterMenu.onDialogOpen).
  const syncStagedFromCommitted = () => {
    setStagedIds(selectedOrderPageIds);
    setStagedEmployees(showEmployees);
    setStagedShifts(showShifts);
    setStagedNotes(showNotes);
    setStagedUnavailabilities(showUnavailabilities);
    setStagedOnlyMine(showOnlyMine);
  };

  // On desktop: live context state. On mobile: staged state.
  const currentIds = isDesktop ? selectedOrderPageIds : stagedIds;
  const currentEmployees = isDesktop ? showEmployees : stagedEmployees;
  const currentShifts = isDesktop ? showShifts : stagedShifts;
  const currentNotes = isDesktop ? showNotes : stagedNotes;
  const currentUnavailabilities = isDesktop ? showUnavailabilities : stagedUnavailabilities;
  const currentOnlyMine = isDesktop ? showOnlyMine : stagedOnlyMine;

  const setCurrentIds = (ids: string[] | null) => (isDesktop ? setSelectedOrderPageIds(ids) : setStagedIds(ids));
  const setCurrentEmployees = isDesktop ? setShowEmployees : setStagedEmployees;
  const setCurrentShifts = isDesktop ? setShowShifts : setStagedShifts;
  const setCurrentNotes = isDesktop ? setShowNotes : setStagedNotes;
  const setCurrentUnavailabilities = isDesktop ? setShowUnavailabilities : setStagedUnavailabilities;
  const setCurrentOnlyMine = isDesktop ? setShowOnlyMine : setStagedOnlyMine;

  const handleDialogReset = () => {
    setCurrentIds(null);
    setCurrentEmployees(true);
    setCurrentShifts(true);
    setCurrentNotes(true);
    setCurrentUnavailabilities(true);
    setCurrentOnlyMine(false);
  };

  const handleApply = () => {
    setSelectedOrderPageIds(stagedIds);
    setShowEmployees(stagedEmployees);
    setShowShifts(stagedShifts);
    setShowNotes(stagedNotes);
    setShowUnavailabilities(stagedUnavailabilities);
    setShowOnlyMine(stagedOnlyMine);
  };

  return (
    <FilterMenu
      name="Calendar"
      isActive={isFilterActive}
      summaryLabel={isFilterActive ? summary.workshops : t('calendar.filter.label')}
      secondaryLabel={isFilterActive ? summary.staff : ''}
      onReset={resetFilter}
      onDialogReset={handleDialogReset}
      onDialogOpen={syncStagedFromCommitted}
      onApply={isDesktop ? undefined : handleApply}
      iconOnly={iconOnly}>
      <List disablePadding>
        <WorkshopsFilterSection
          account={account}
          selectedIds={currentIds}
          onChange={setCurrentIds}
          showEmployeesLocationHint
        />

        <ListItem>
          <Typography variant="overline" color="text.secondary">
            {t('calendar.filter.staff')}
          </Typography>
        </ListItem>
        <ListItem>
          <Stack width="100%" gap={0.5}>
            <FormControlLabel
              label={t('calendar.filter.employees')}
              control={
                <Switch
                  size={isDesktop ? 'small' : 'medium'}
                  checked={currentEmployees}
                  onChange={(e) => setCurrentEmployees(e.target.checked)}
                />
              }
            />
            <FormControlLabel
              label={t('calendar.filter.shifts')}
              control={
                <Switch
                  size={isDesktop ? 'small' : 'medium'}
                  checked={currentShifts}
                  onChange={(e) => setCurrentShifts(e.target.checked)}
                />
              }
            />
            <FormControlLabel
              label={t('calendar.filter.notes')}
              control={
                <Switch
                  size={isDesktop ? 'small' : 'medium'}
                  checked={currentNotes}
                  onChange={(e) => setCurrentNotes(e.target.checked)}
                />
              }
            />
            <FormControlLabel
              label={t('calendar.filter.unavailabilities')}
              control={
                <Switch
                  size={isDesktop ? 'small' : 'medium'}
                  checked={currentUnavailabilities}
                  onChange={(e) => setCurrentUnavailabilities(e.target.checked)}
                />
              }
            />
            <FormControlLabel
              sx={{ mt: 1 }}
              label={t('calendar.filter.only_mine')}
              control={
                <Switch
                  size={isDesktop ? 'small' : 'medium'}
                  checked={currentOnlyMine}
                  onChange={(e) => setCurrentOnlyMine(e.target.checked)}
                />
              }
            />
          </Stack>
        </ListItem>
      </List>
    </FilterMenu>
  );
};
